import type {
  ContractKind,
  ContractResponse,
  LookupContractEntry,
  RosterContractEntry,
} from '../../api';
import { formatCpf, formatDate, formatInteger } from '../../format';
import { COMPANY_BASELINE_COPY } from './baselineImport';
import { KIND_LABELS, ONBOARDING_COPY } from './onboarding';

/**
 * What the Segurados panel on the staff company page says, and how it names a
 * contract.
 *
 * The panel answers the phone call "is this person registered with you?", so
 * its copy points at the two places on the same page where the answer comes
 * from when it is "no": the contract that has to exist, and the import that
 * fills it.
 */

export const SEGURADOS_COPY = {
  title: 'Segurados',
  description:
    'Quem está coberto hoje por qualquer um dos contratos desta empresa. ' +
    `"${COMPANY_BASELINE_COPY.action}" lê a planilha do cliente e leva cada linha ao contrato ` +
    'desta empresa com o mesmo Subestipulante e Módulo; ninguém é cadastrado fora de um contrato.',
  loading: 'Carregando segurados…',
  error: 'Não foi possível carregar os segurados',
  contractsLoading: ONBOARDING_COPY.contractsLoading,
  contractsError: ONBOARDING_COPY.contractsError,
  noContracts: 'Nenhum contrato, nenhum segurado',
  noContractsDescription: `Cadastre o primeiro contrato em "${ONBOARDING_COPY.createContract}", no quadro Contratos acima. Os segurados aparecem aqui depois que a população dele for importada.`,
  empty: 'Ninguém coberto hoje',
  emptyDescription: `Os contratos existem, mas ainda não têm população. Importe a planilha da empresa em "${COMPANY_BASELINE_COPY.action}", no alto deste quadro.`,
  noMatches: 'Ninguém corresponde aos filtros.',
  noMatchesHint:
    'Confira a grafia do nome, os números do CPF e os filtros escolhidos, ou use "Limpar filtros".',
  // The filter row: a name or a CPF can be typed in part, the CPF with or without punctuation.
  nameFilterPlaceholder: 'Parte do nome',
  cpfFilterPlaceholder: 'Números do CPF',
  withScheduled: 'Com movimentação agendada',
  withoutScheduled: 'Sem movimentação agendada',
  caption: 'Pessoas cobertas pelos contratos da empresa nesta data',
  scheduledUnavailable:
    'Não foi possível ler as movimentações agendadas. A lista acima não marca quem tem uma ' +
    'exclusão ou alteração pedida; confira na fila de revisão antes de responder.',
  nameHeader: 'Nome',
  cpfHeader: 'CPF',
  birthDateHeader: 'Nascimento',
  salaryHeader: 'Salário',
  coverageFromHeader: 'Início da cobertura',
  coverageToHeader: 'Fim da cobertura',
  scheduledHeader: 'Movimentação agendada',
  contractsHeader: 'Contratos',
  companyHeader: 'Empresa',
  unknownCompany: 'Empresa fora do grupo',
} as const;

/** "1 pessoa coberta", "12 pessoas cobertas". */
export function coveredCount(n: number): string {
  return `${formatInteger(n)} ${n === 1 ? 'pessoa coberta' : 'pessoas cobertas'}`;
}

function kindWord(kind: ContractKind): string {
  return KIND_LABELS[kind] ?? kind;
}

/**
 * One contract as a row's tag names it: the kind first, because "vida" and
 * "saúde" are what tell a person's two contracts apart, then the insurer.
 *
 * The contract comes from the company's listing; a roster entry whose contract
 * is not in it (the listing still loading, say) is named by its kind alone
 * rather than not at all.
 */
export function entryLabel(
  entry: RosterContractEntry,
  contracts: ReadonlyMap<string, ContractResponse>,
): string {
  const contract = contracts.get(entry.contract_id);
  const kind = kindWord(entry.contract_kind);
  return contract === undefined ? kind : `${kind} · ${contract.insurer.name}`;
}

/** One contract as the select offers it: kind, insurer, and the apólice when there is one. */
export function contractOptionLabel(contract: ContractResponse): string {
  const parts = [kindWord(contract.kind), contract.insurer.name];
  if (contract.apolice !== null && contract.apolice !== undefined && contract.apolice !== '') {
    parts.push(`apólice ${contract.apolice}`);
  }
  return parts.join(' · ');
}

// --- One segurado (#418) ----------------------------------------------------

/**
 * What the staff person page says.
 *
 * The page answers "when was this changed?" on the phone, so every table keeps
 * its "Registrado em" column -- that column *is* the answer -- and the copy
 * names each block by the contract a customer would name it by.
 */
export const PERSON_COPY = {
  title: 'Segurado',
  loading: 'Carregando segurado…',
  error: 'Não foi possível carregar o segurado',
  notFound: 'Segurado não encontrado',
  notFoundHint:
    'Ninguém com este identificador está em um contrato desta empresa. O endereço pode estar ' +
    'incompleto, ou ser de outra empresa.',
  backToSegurados: 'Voltar aos segurados da empresa',
  historyLoading: 'Carregando histórico…',
  historyError: 'Não foi possível carregar o histórico deste contrato',
  coveredToday: 'Coberto hoje',
  notCoveredToday: 'Sem cobertura hoje',
  insurerTitle: 'Dados da seguradora',
  certificateLabel: 'Certificado',
  insuredCapitalLabel: 'IS Segurado',
  premiumLabel: 'Prêmio',
  coverageTitle: 'Períodos de cobertura',
  coverageCaption: 'Períodos de cobertura neste contrato',
  coverageEmpty: 'Nenhum período de cobertura registrado',
  salaryTitle: 'Salários',
  salaryCaption: 'Versões de salário neste contrato',
  salaryEmpty: 'Nenhum salário registrado',
  leaveTitle: 'Afastamentos',
  leaveCaption: 'Afastamentos neste contrato',
  leaveEmpty: 'Nenhum afastamento registrado',
  startHeader: 'Início',
  endHeader: 'Fim',
  fromHeader: 'A partir de',
  salaryHeader: 'Salário',
  recordedAtHeader: 'Registrado em',
  openReturn: 'Sem data de retorno',
} as const;

/** "CPF 529.982.247-25 · nascimento em 17/04/1988". */
export function personDescription(cpf: string, birthDate: string): string {
  return `CPF ${formatCpf(cpf)} · nascimento em ${formatDate(birthDate)}`;
}

/**
 * One of a person's contracts as its block names it: kind, insurer, then the
 * subestipulante and módulo the insurer files it under. The insurer comes from
 * the company's listing and is left out, rather than guessed, while it loads.
 */
export function personContractLabel(
  entry: LookupContractEntry,
  contract: ContractResponse | undefined,
): string {
  const parts = [kindWord(entry.contract_kind)];
  if (contract !== undefined) {
    parts.push(contract.insurer.name);
  }
  parts.push(`subestipulante ${String(entry.subestipulante)}`, `módulo ${String(entry.modulo)}`);
  return parts.join(' · ');
}
